import { useState, useEffect, useRef, Suspense } from 'react';
import DocumentInviteModal from 'components/editor/modals/DocumentInviteModal/DocumentInviteModal';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { ErrorBoundary } from 'react-error-boundary';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useQuery } from '@tanstack/react-query';
import { usersPermissionsQueries } from 'services/access-control/users/usersPermissionsQueries';
import type { ApprovalAccess } from 'hooks/useApprovalAccess';
import { ApprovalActionPlaceholder } from './ApprovalActionPlaceholder';
import { ApprovalOrShareAction } from './ApprovalOrShareAction';

interface ShareDocumentV3Props {
  documentTitleWithCompanyName: string;
  previewURL: string;
  canShare: boolean;
  approvalAccess: ApprovalAccess;
}

export function ShareDocumentV3({ documentTitleWithCompanyName, previewURL, canShare, approvalAccess }: ShareDocumentV3Props) {
  const { t } = useTranslation();
  const contentId = useAppSelector(selectContentId);

  // Collaborator invites are gated on documents_edit: legacy no longer performs a server-side
  // document-permission check for V3 documents, so this is the authorization gate for inviting.
  const { data: permissionsResult } = useQuery(usersPermissionsQueries.permissions);
  const canInviteCollaborators = permissionsResult?.permissions.edit_documents.allowed ?? false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSharePopperVisible, setIsSharePopperVisible] = useState(false);
  const [isCollaboratorModalVisible, setIsCollaboratorModalVisible] = useState(false);
  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSharePopperVisible &&
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setAnchorEl(null);
        setIsSharePopperVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSharePopperVisible, anchorEl]);

  const openCollaboratorModal = () => {
    handlePopper();
    setIsCollaboratorModalVisible(true);
  };

  const handlePopper = (event?: React.MouseEvent<HTMLElement>) => {
    if (anchorEl || !event) {
      setAnchorEl(null);
      setIsSharePopperVisible(false);
    } else {
      setAnchorEl(event.currentTarget);
      setIsSharePopperVisible(true);
    }
  };

  const handlePreviewRedirect = () => window.open(previewURL, '_blank');

  return (
    <>
      <Button variant="text" color="primary" className="preview-button" onClick={handlePreviewRedirect}>
        {t('preview')}
      </Button>
      {canInviteCollaborators && (
        <Button variant="text" color="primary" className="invite-button" onClick={openCollaboratorModal}>
          {t('button.invite')}
        </Button>
      )}
      <ErrorBoundary fallbackRender={() => <ApprovalActionPlaceholder pulse={false} />}>
        <Suspense fallback={<ApprovalActionPlaceholder />}>
          <ApprovalOrShareAction
            contentId={contentId}
            previewURL={previewURL}
            documentTitleWithCompanyName={documentTitleWithCompanyName}
            canShare={canShare}
            approvalAccess={approvalAccess}
          />
        </Suspense>
      </ErrorBoundary>
      {canInviteCollaborators && (
        <DocumentInviteModal
          isOpen={isCollaboratorModalVisible}
          setIsOpen={setIsCollaboratorModalVisible}
          documentName={documentTitleWithCompanyName}
        />
      )}
    </>
  );
}
